import React, { FormEvent, useEffect, useState, useRef } from 'react';
import { CustomerModel } from '../models/CustomerModel';
import { set } from 'date-fns';
import customerService from '../services/customerService';


interface EditCustomerCompProps {
    show: boolean;
    customer: CustomerModel;
    onClose: () => void;
    onCustomerUpdated: (updatedCustomer: CustomerModel) => void;
}

interface CustomerFormErrors {
    firstname?: string;
    lastname?: string;
    companyName?: string;
    contactNumber?: string;
    notes?: string;

}

const EditCustomerComp: React.FC<EditCustomerCompProps> = ({ show, customer, onClose, onCustomerUpdated: onCustomerUpdated }) => {

    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);


    const [firstName, setFirstName] = useState(customer.firstname);
    const [lastName, setLastName] = useState(customer.lastname);
    const [companyName, setCompanyName] = useState(customer.companyName);
    const [contactNumber, setContactNumber] = useState(customer.contactNumber);
    const [notes, setNotes] = useState(customer.notes || '');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customerFormErrors, setCustomerFormErrors] = useState<CustomerFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setFirstName(customer.firstname);
            setLastName(customer.lastname);
            setCompanyName(customer.companyName || '');
            setContactNumber(customer.contactNumber || '');
            setNotes(customer.notes || '');

            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateCustomer = () => {
        let errors: CustomerFormErrors = {};
        if (!firstName.trim()) {
            errors.firstname = 'First name is required.';
        }

        if (firstName.length > 50) {
            errors.firstname = 'First name cannot exceed 50 characters.';
        }

        if (!lastName.trim()) {
            errors.lastname = 'Last name is required.';
        }

        if (lastName.length > 50) {
            errors.lastname = 'Last name cannot exceed 50 characters.';
        }

        if (!companyName.trim()) {
            errors.companyName = 'Company name is required.';
        }

        if (companyName.length > 50) {
            errors.companyName = 'Company name cannot exceed 50 characters.';
        }

        if (!contactNumber.trim()) {
            errors.contactNumber = 'Contact number is required.';
        }

        if (contactNumber.length > 50) {
            errors.contactNumber = 'Contact number cannot exceed 50 numeric numbers.';
        }

        if (!/^[0-9()\-\s]+$/.test(contactNumber)) {
            errors.contactNumber = 'Contact number must contain only numbers, spaces, parentheses, or hyphens.';
        }

        if (notes.length > 500) {
            errors.notes = 'Notes cannot exceed 500 characters.';
        }

        setCustomerFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleCustomerSubmit = (e: FormEvent) => {
        const updateCustomer = async () => {
            try {
                const updateSucceed = await customerService.updateCustomer(customer.sellerCustomerId, {
                    firstname: firstName,
                    lastname: lastName,
                    companyName: companyName,
                    contactNumber: contactNumber,
                    notes: notes
                });
                if (updateSucceed === "Success") {
                    const updatedCustomer: CustomerModel = {
                        ...customer,
                        firstname: firstName,
                        lastname: lastName,
                        companyName: companyName,
                        contactNumber: contactNumber,
                        notes: notes
                    };
                    onCustomerUpdated(updatedCustomer);
                } else {
                    setServiceStatus({ type: "Error", message: "Failed to update the customer." });
                }
            }
            catch (err) {
                setServiceStatus({ type: "Error", message: "Failed to update the customer." });
            }
            setIsSubmitting(false);
        };
        e.preventDefault();
        setServiceStatus({ type: "", message: "" });
        if (validateCustomer()) {
            setIsSubmitting(true);
            updateCustomer();
        }
    };

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Customer</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => bsModalRef.current?.hide()}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className='row g-3'>
                            <div className="col-md-12">
                                <label className="form-label">First Name</label>
                                <input
                                    type="text"
                                    className={`form-control ${customerFormErrors.firstname ? 'is-invalid' : ''}`}
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                />
                                {customerFormErrors.firstname && <div className="invalid-feedback">{customerFormErrors.firstname}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Last Name</label>
                                <input
                                    type="text"
                                    className={`form-control ${customerFormErrors.lastname ? 'is-invalid' : ''}`}
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                                {customerFormErrors.lastname && <div className="invalid-feedback">{customerFormErrors.lastname}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Company Name</label>
                                <input
                                    type="text"
                                    className={`form-control ${customerFormErrors.companyName ? 'is-invalid' : ''}`}
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                />
                                {customerFormErrors.companyName && <div className="invalid-feedback">{customerFormErrors.companyName}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Contact Number</label>
                                <input
                                    type="text"
                                    className={`form-control ${customerFormErrors.contactNumber ? 'is-invalid' : ''}`}
                                    value={contactNumber}
                                    onChange={(e) => setContactNumber(e.target.value)}
                                />
                                {customerFormErrors.contactNumber && <div className="invalid-feedback">{customerFormErrors.contactNumber}</div>}
                            </div>
                            <div className="col-md-12">
                                <label className="form-label">Notes</label>
                                <textarea className={`form-control ${customerFormErrors.notes ? 'is-invalid' : ''}`} rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} ></textarea>
                                {customerFormErrors.notes && <div className="invalid-feedback">{customerFormErrors.notes}</div>}
                            </div>

                            {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                        </div>
                        <div className="modal-footer px-0 d-flex justify-content-between">
                            <button className="btn btn-outline-dark rounded-0" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-dark rounded-0" onClick={handleCustomerSubmit} disabled={isSubmitting}>
                                {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                            </button>
                        </div>
                    </div>
                </div >
            </div >
        </div>
    );

};

export default EditCustomerComp;